/**
 * /academy/persona — PRSVR Student Assessment Matrix
 *
 * A 3-test gamified assessment flow for scholar-athletes (grades 5-8):
 *   1. Mindset & Personality  → Avatar
 *   2. Superpowers            → Badges
 *   3. Learning Style         → Archetype
 *
 * Each test uses the same runner: intro card → question stepper → result card.
 * When all three are complete, the composed persona summary renders.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ChevronRight, CheckCircle2, Sparkles, Trophy, Brain, Zap, BookOpen, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const TEST_META = {
  mindset:     { icon: Brain,    accent: '#8B5CF6', name: 'Mindset & Personality',   short: 'The Avatar Test' },
  superpowers: { icon: Zap,      accent: '#F59E0B', name: 'Superpowers',             short: 'Find Your Badge' },
  learning:    { icon: BookOpen, accent: '#10B981', name: 'Learning Style',          short: 'How You Learn Best' },
};

const PrsvrAssessments = () => {
  const [tests, setTests] = useState([]);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null); // 'mindset' | 'superpowers' | 'learning' | null
  const [isSignedIn, setIsSignedIn] = useState(!!localStorage.getItem('academy_token'));

  const refreshPersona = useCallback(async () => {
    try {
      const r = await api.get('/academy/prsvr-assessments/persona', { headers: authHeaders() });
      setPersona(r.data);
      setIsSignedIn(true);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) setIsSignedIn(false);
    }
  }, []);

  useEffect(() => {
    api.get('/academy/prsvr-assessments/tests')
      .then((r) => setTests(r.data.tests || []))
      .finally(() => setLoading(false));
    refreshPersona();
  }, [refreshPersona]);

  const onTestFinished = () => {
    setActiveKey(null);
    refreshPersona();
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white pt-24 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;
  }

  if (activeKey) {
    return <TestRunner testKey={activeKey} onBack={() => setActiveKey(null)} onFinished={onTestFinished} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white pt-16 pb-24" data-testid="prsvr-assessments">
      <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: 'linear-gradient(180deg, rgba(139,92,246,0.08), transparent)' }}>
        <div className="container mx-auto max-w-5xl">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-purple-400">PRSVR Persona Studio</div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2 break-words">
            Discover Your <span className="text-purple-400">Scholar-Athlete Persona</span>
          </h1>
          <p className="text-sm md:text-base text-zinc-300 mt-3 max-w-2xl leading-relaxed">
            Three quick assessments. One 1-page persona report — your Avatar, your Superpowers, and how you learn best.
            Feeds into your on-court, on-camera, and classroom identity.
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-6 py-8 space-y-8">
        {!isSignedIn && (
          <div className="border-2 border-amber-500 bg-amber-500/[0.08] px-5 py-4 flex items-start gap-3 flex-wrap" data-testid="persona-signin-banner">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-400">Sign in to save your results</div>
              <div className="text-sm text-white font-bold mt-1">You can browse the tests, but you need to be signed in to save your Persona and unlock the composed summary.</div>
            </div>
            <a href={`/login?redirect=${encodeURIComponent('/academy/persona')}`}
               className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 text-[11px] font-black uppercase tracking-widest"
               data-testid="persona-signin-btn">Sign in</a>
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-4">
          {tests.map((t) => {
            const meta = TEST_META[t.key];
            const Icon = meta?.icon || Sparkles;
            const done = persona?.completed?.[t.key];
            return (
              <button key={t.key} onClick={() => setActiveKey(t.key)}
                className="border-2 border-zinc-800 bg-zinc-950 p-5 text-left hover:border-purple-500 transition-colors relative min-w-0 overflow-hidden"
                style={{ borderColor: done ? `${meta?.accent || '#8B5CF6'}80` : undefined }}
                data-testid={`test-card-${t.key}`}>
                {done && <span className="absolute top-2 right-2 text-[9px] font-black uppercase tracking-widest text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Done</span>}
                <div className="w-10 h-10 flex items-center justify-center mb-3" style={{ backgroundColor: `${meta?.accent || '#8B5CF6'}22`, color: meta?.accent || '#8B5CF6' }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-[10px] uppercase tracking-widest font-black" style={{ color: meta?.accent || '#8B5CF6' }}>{meta?.short}</div>
                <div className="text-white font-black text-lg mt-1 leading-tight">{t.title}</div>
                <div className="text-[11px] text-zinc-500 mt-1">{t.kicker}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-purple-300">
                  {done ? 'Retake' : 'Start'} <ChevronRight className="w-3 h-3" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Persona summary */}
        {persona?.summary && <PersonaSummary summary={persona.summary} />}
        {persona && !persona.summary && (
          <div className="border-2 border-purple-500/40 bg-purple-500/5 px-5 py-4 text-sm text-purple-200">
            Finish all three tests to unlock your composed persona statement + growth plan.
          </div>
        )}
      </div>
    </div>
  );
};


const TestRunner = ({ testKey, onBack, onFinished }) => {
  const [test, setTest] = useState(null);
  const [loadError, setLoadError] = useState(null); // 'auth' | 'network' | null
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const meta = TEST_META[testKey];

  useEffect(() => {
    setLoadError(null);
    setTest(null);
    api.get(`/academy/prsvr-assessments/test/${testKey}`, { headers: authHeaders() })
      .then((r) => setTest(r.data))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setLoadError('auth');
          toast.error('Please sign in to take the assessment.');
        } else {
          setLoadError('network');
          toast.error('Could not load the test. Please try again.');
        }
      });
  }, [testKey]);

  const pick = (qid, i) => {
    setAnswers((a) => ({ ...a, [qid]: i }));
    setTimeout(() => setIdx((n) => Math.min(n + 1, (test?.questions.length || 1) - 1)), 200);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/academy/prsvr-assessments/submit/${testKey}`, { answers }, { headers: authHeaders() });
      setResult(r.data.result);
      toast.success('Locked in — badge earned.');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) toast.error('Please sign in to save your result.');
      else toast.error('Could not submit. Try again.');
    }
    finally { setSubmitting(false); }
  };

  // ─── Error state — friendly recovery instead of an infinite spinner ────
  if (loadError) {
    const isAuth = loadError === 'auth';
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white pt-24 px-6 pb-24" data-testid="test-error">
        <div className="container mx-auto max-w-lg">
          <button onClick={onBack} className="text-[10px] uppercase tracking-widest font-black text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Studio
          </button>
          <div className="border-2 border-purple-500/40 bg-purple-500/[0.06] p-6">
            <div className="text-[10px] uppercase tracking-[0.4em] font-black text-purple-400">
              {isAuth ? 'Sign in to continue' : "Couldn't load"}
            </div>
            <h2 className="text-2xl font-black text-white leading-tight mt-2">
              {isAuth ? 'Your session ended — sign in and we\'ll bring you right back.' : 'The test didn\'t load. Check your connection and try again.'}
            </h2>
            <div className="mt-5 flex gap-2 flex-wrap">
              {isAuth ? (
                <button
                  onClick={() => { window.location.href = `/login?redirect=${encodeURIComponent('/academy/persona')}`; }}
                  className="bg-purple-500 hover:bg-purple-400 text-black px-5 py-3 text-[11px] font-black uppercase tracking-widest"
                  data-testid="test-error-signin"
                >
                  Sign in
                </button>
              ) : (
                <button
                  onClick={() => { setLoadError(null); setTest(null); setIdx(0); setAnswers({}); api.get(`/academy/prsvr-assessments/test/${testKey}`, { headers: authHeaders() }).then((r) => setTest(r.data)).catch(() => setLoadError('network')); }}
                  className="bg-purple-500 hover:bg-purple-400 text-black px-5 py-3 text-[11px] font-black uppercase tracking-widest"
                  data-testid="test-error-retry"
                >
                  Try again
                </button>
              )}
              <button onClick={onBack} className="border-2 border-zinc-700 text-zinc-300 hover:border-white hover:text-white px-5 py-3 text-[11px] font-black uppercase tracking-widest">
                Back to Studio
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!test) return <div className="min-h-screen bg-black text-white pt-24 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-purple-400" /></div>;

  if (result) {
    return (
      <div className="min-h-screen bg-[#0a0a12] text-white pt-24 px-6 pb-24">
        <div className="container mx-auto max-w-2xl">
          <div className="border-2 p-8" style={{ borderColor: meta.accent, backgroundColor: `${meta.accent}0F` }} data-testid="test-result">
            <div className="text-[10px] uppercase tracking-[0.4em] font-black" style={{ color: meta.accent }}>{meta.short} · Result</div>
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight mt-2 break-words">{summarizeResult(testKey, result)}</h2>
            <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{summarizeResultBody(testKey, result)}</p>
          </div>
          <div className="mt-6 flex gap-2 flex-wrap">
            <button onClick={onFinished} className="bg-purple-500 hover:bg-purple-400 text-black px-5 py-3 text-[11px] font-black uppercase tracking-widest" data-testid="test-continue-btn">
              Back to Studio <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = test.questions[idx];
  const progress = ((idx + (answers[q.id] != null ? 1 : 0)) / test.questions.length) * 100;
  return (
    <div className="min-h-screen bg-[#0a0a12] text-white pt-20 px-6 pb-24">
      <div className="container mx-auto max-w-2xl">
        <button onClick={onBack} className="text-[10px] uppercase tracking-widest font-black text-zinc-500 hover:text-white flex items-center gap-1 mb-4" data-testid="test-back-btn">
          <ArrowLeft className="w-3 h-3" /> Studio
        </button>
        <div className="text-[10px] uppercase tracking-[0.4em] font-black mb-3" style={{ color: meta.accent }}>{meta.name} · Question {idx + 1} / {test.questions.length}</div>
        <div className="w-full h-1.5 bg-black">
          <div className="h-full transition-all" style={{ width: `${progress}%`, backgroundColor: meta.accent }} />
        </div>

        <h2 className="text-xl md:text-2xl font-black text-white leading-tight mt-6 break-words" data-testid="test-q-prompt">{q.prompt}</h2>

        <div className="mt-6 space-y-3">
          {q.options.map((o, i) => {
            const selected = answers[q.id] === i;
            return (
              <button key={i} onClick={() => pick(q.id, i)}
                className="w-full text-left border-2 px-4 py-3 transition-colors min-w-0 overflow-hidden break-words"
                style={{
                  borderColor: selected ? meta.accent : '#1f1f2b',
                  backgroundColor: selected ? `${meta.accent}18` : '#0a0a12',
                }}
                data-testid={`test-q-${q.id}-opt-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 border-2 flex items-center justify-center text-[10px] font-black mt-0.5"
                    style={{ borderColor: selected ? meta.accent : '#3f3f4b', color: selected ? meta.accent : '#71717a' }}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-sm text-white leading-relaxed">{o.label}</span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button onClick={() => setIdx((n) => Math.max(0, n - 1))} disabled={idx === 0}
            className="border-2 border-zinc-800 text-zinc-400 hover:border-white hover:text-white disabled:opacity-30 px-3 py-2 text-[10px] font-black uppercase tracking-widest">
            Prev
          </button>
          {idx < test.questions.length - 1 ? (
            <button onClick={() => setIdx((n) => Math.min(test.questions.length - 1, n + 1))}
              disabled={answers[q.id] == null}
              className="border-2 border-zinc-800 text-zinc-400 hover:border-white hover:text-white disabled:opacity-30 px-3 py-2 text-[10px] font-black uppercase tracking-widest">
              Next
            </button>
          ) : (
            <button onClick={submit}
              disabled={submitting || Object.keys(answers).length < test.questions.length}
              className="text-black px-5 py-3 text-[11px] font-black uppercase tracking-widest disabled:opacity-40"
              style={{ backgroundColor: meta.accent }}
              data-testid="test-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} Lock in my result
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const summarizeResult = (key, r) => {
  if (key === 'mindset') return ({ architect: 'THE ARCHITECT', catalyst: 'THE CATALYST', anchor: 'THE ANCHOR', innovator: 'THE INNOVATOR' })[r] || 'Persona';
  if (key === 'superpowers') return `TOP BADGE — ${(r?.primary || '').toUpperCase()}`;
  if (key === 'learning') return `${({V:'VISUAL MAPPING',A:'AUDIO-COMMAND',R:'NOTEBOOK & SCRIPT',K:'SPATIAL / VR'})[r?.primary] || ''}`;
  return '';
};

const summarizeResultBody = (key, r) => {
  if (key === 'mindset') return ({
    architect: 'You see the game 3 moves ahead — the strategic, quiet floor general your team leans on for reads.',
    catalyst: 'You flip momentum — the vocal, high-energy presence that turns cold gyms into fire drills.',
    anchor: 'You are the rock — steady, reliable, the teammate everyone trusts when the pressure spikes.',
    innovator: 'You thrive in chaos — the creative playmaker who wins moments nobody else sees coming.',
  })[r] || '';
  if (key === 'superpowers') return `Your top 3: ${(r?.top3 || []).map(s => s.toUpperCase()).join(' · ')}. Blind spot to guard: over-relying on your #1.`;
  if (key === 'learning') return `You learn best through this mode. Head over to your persona summary to see the study method built for you.`;
  return '';
};

const PersonaSummary = ({ summary }) => (
  <div className="border-2 border-purple-500 bg-gradient-to-br from-purple-500/[0.08] to-transparent p-6" data-testid="persona-summary">
    <div className="text-[10px] uppercase tracking-[0.4em] font-black text-purple-400 mb-2">Your Scholar-Athlete Persona</div>
    <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight break-words">{summary.avatar?.name}</h2>
    <p className="text-sm text-zinc-300 italic mt-1">{summary.avatar?.tagline}</p>

    <div className="mt-5 grid md:grid-cols-3 gap-4">
      <SummaryBlock label="Top Superpowers" tone="#F59E0B" items={summary.superpowers?.top3 || []} />
      <SummaryBlock label="Signature Strengths" tone="#10B981" items={summary.avatar?.strengths || []} />
      <SummaryBlock label="Learning Style" tone="#22D3EE" items={[summary.learning?.name, summary.learning?.tagline].filter(Boolean)} />
    </div>

    <div className="mt-5 border-2 border-orange-500/40 bg-orange-500/5 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest font-black text-orange-400">Growth Area</div>
      <p className="text-sm text-zinc-100 mt-1 leading-relaxed">{summary.avatar?.growth}</p>
    </div>

    <div className="mt-4 border-2 border-cyan-500/40 bg-cyan-500/5 px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest font-black text-cyan-400">Recommended Study Method</div>
      <p className="text-sm text-zinc-100 mt-1 leading-relaxed">{summary.learning?.study}</p>
    </div>

    <div className="mt-5 border-l-2 border-purple-500 pl-4">
      <div className="text-[10px] uppercase tracking-widest font-black text-purple-400 mb-1">Your On-Camera Persona Statement</div>
      <p className="text-base text-white leading-relaxed italic">"{summary.persona_statement}"</p>
    </div>
  </div>
);

const SummaryBlock = ({ label, items, tone }) => (
  <div className="border-2 p-4 min-w-0" style={{ borderColor: `${tone}44`, backgroundColor: `${tone}0A` }}>
    <div className="text-[9px] uppercase tracking-widest font-black" style={{ color: tone }}>{label}</div>
    <ul className="mt-2 space-y-1">
      {items.map((it, i) => <li key={i} className="text-[13px] text-white leading-snug break-words">• {it}</li>)}
    </ul>
  </div>
);

export default PrsvrAssessments;
